import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../api";
import { HERO_SLIDE_DEFAULTS } from "../data/heroSlides";

const SettingsContext = createContext(null);

const DEFAULT_BRAND = {
  brand_name: "LALITA SOUJANYA",
  whatsapp_number: "+918309550349",
  instagram_handle: "lalitasoujanya_23",
  email: "official@lalitasoujanya.com",
  announcement_bar: "Enjoy 10% Off Your First Order",
  announcement_active: true,
  launch_offer_active: true,
  launch_offer_text: "Launch edit 2026 · Limited pieces",
  logo_url: null,
  footer_note: "Limited pieces, made for the moments you keep.",
  hero_image: null,
  editorial_banner_image: null,
  newsletter_heading: "Join the house.",
  newsletter_body: "Private previews, launch invitations and the occasional note from the studio.",
  newsletter_button: "Subscribe",
};

const DEFAULT_HOMEPAGE = {
  hero: {
    visible: true,
    image: null,
    eyebrow: "The Modern Trousseau — 2026",
    title_line1: "LALITA",
    title_line2: "SOUJANYA",
    subtitle: "Crafted thoughtfully. Worn beautifully.",
    description: "A considered edit of Indian occasionwear, shaped in colour, movement and quiet confidence.",
    primary_cta_label: "Shop the collection",
    primary_cta_link: "/shop",
    secondary_cta_label: "Explore the edit",
    secondary_cta_link: "/shop?category=New%20Arrivals",
    slides: HERO_SLIDE_DEFAULTS,
  },
  product_shelf: { visible: true, eyebrow: "The house selection", heading: "Most-loved looks.", view_all_label: "View the complete collection", view_all_link: "/shop", empty_message: "New pieces for this edit are arriving soon." },
  assurance: { visible: true, eyebrow: "Atelier assurance", heading: "Considered at every step.", atelier_title: "Hyderabad atelier", atelier_body: "Contemporary Indian occasionwear, considered close to the craft.", atelier_link: "/about", sizing_title: "Custom sizing", sizing_body: "Personalised sizing is available on request and subject to design feasibility.", sizing_link: "/customization", dispatch_title: "Considered dispatch", dispatch_body: "Ready pieces are prepared for dispatch within five to seven business days.", dispatch_link: "/contact", care_title: "Direct studio care", care_body: "Every order is personally confirmed with the studio.", whatsapp_message: "Hi Lalita Soujanya, I'd like help with an order." },
  manifesto: { visible: true, eyebrow: "Contemporary Indian dressing", heading: "Heritage, composed for now.", body: "Lalita Soujanya brings Indian craftsmanship into a modern wardrobe through fluid silhouettes, refined detail and pieces designed to feel as effortless as they are expressive.", note: "Created for celebrations, personal rituals and every moment that calls for something distinctly your own.", cta_label: "Discover the house", cta_link: "/about" },
  brand_story: {
    visible: true,
    eyebrow: "The house note",
    heading: "Made by hands. Worn by presence.",
    body:
      "Every Lalita Soujanya piece begins in our Hyderabad atelier, where old-world craft meets a modern silhouette. We work with heritage textile artisans to make couture that feels like an heirloom you actually wear.",
    image: null,
    cta_label: "Our craft",
    cta_link: "/about",
    image_caption: "Atelier notes / Hyderabad",
    principle1: "Indian craft",
    principle2: "Modern form",
    principle3: "Personal expression",
  },
  categories: { visible: true, eyebrow: "01 · Navigate", heading: "Find your occasion.", view_all_label: "View all looks", view_all_link: "/shop", card_eyebrow: "Explore" },
  top_rated: { visible: true, eyebrow: "House favourites", heading: "Top rated.", description: "The pieces the studio and our clients love most.", cta_label: "Shop all", cta_link: "/shop" },
  featured: { visible: true, eyebrow: "02 · The edit", heading: "Featured looks.", description: "Six pieces the studio is holding closest this season.", cta_label: "Shop all", cta_link: "/shop" },
  new_arrivals: { visible: true, eyebrow: "03 · Just in", heading: "New arrivals.", description: "The most recent additions to the house.", cta_label: "View arrivals", cta_link: "/shop" },
  discovery: { visible: true, eyebrow: "The private edit", heading: "Discovered for you.", generic_message: "A fresh rotation from across the house, selected to make every visit feel new.", personalized_message: "Inspired by your interest in {category}, with a few unexpected finds.", refresh_note: "Selections refresh as you explore", swipe_label: "Swipe to discover" },
  house_codes: { visible: true, eyebrow: "The Lalita Soujanya point of view", heading: "Made with intention. Worn with instinct.", item1_title: "Craft, reinterpreted", item1_body: "Indian embroideries, textures and artisanal techniques are considered through a distinctly contemporary lens.", item2_title: "Movement, always", item2_body: "Fluid forms and balanced construction let every silhouette feel expressive, comfortable and effortless to wear.", item3_title: "Distinctly yours", item3_body: "Colour, detail and proportion come together in pieces that celebrate individuality rather than prescribe it." },
  editorial_banner: { visible: true, image: null, edition_left: "Evening edit", edition_right: "LS / 2026", eyebrow: "Designed for the afterglow", title_line1: "The night is", title_line2: "yours.", body: "An evening chapter shaped in wine, ivory and champagne, with pieces that make an entrance and stay in memory.", cta_label: "Shop occasion wear", cta_link: "/shop/womens-wear" },
  private_styling: { visible: true, image: null, eyebrow: "A more personal way to shop", heading: "Your occasion. Your expression.", body: "Looking for a considered silhouette, a particular colour story or a more personal fit? Begin a conversation with the studio and discover pieces selected around the way you want to feel.", point1: "Share your occasion", point2: "Refine the details", point3: "Make it distinctly yours", primary_cta_label: "Explore customisation", primary_cta_link: "/customization", secondary_cta_label: "Speak to the studio", whatsapp_message: "Hi Lalita Soujanya, I'd like help selecting a look for my occasion." },
  old_deals: { visible: true, eyebrow: "Archive · Studio deals", heading: "Old deals, still lovely.", description: "Past-season pieces at a considered price.", banner_image: null, cta_label: "Shop deals", cta_link: "/shop?sort=price_asc" },
  launch_offer: {
    visible: true,
    eyebrow: "For first-time guests",
    heading: "Ten percent off your first order.",
    body: "Because a first look should feel like an occasion. Message us on WhatsApp with the piece you love.",
    cta_label: "Enquire on WhatsApp",
    whatsapp_message: "Hi Lalita Soujanya, I'd like to claim my first-order welcome.",
  },
  instagram: { visible: true, eyebrow: "Follow the house", heading: "@lalitasoujanya_23", body: "Notes from the studio, new arrivals and the people who wear them.", images: [], cta_label: "Follow on Instagram" },
  newsletter: { visible: true, eyebrow: "The house journal", heading: "Join the house.", body: "Private previews, launch invitations and the occasional note from the studio.", button_label: "Subscribe", note: "Occasional notes from the atelier. Unsubscribe at any time." },
  footer: {
    wordmark_line1: "LALITA",
    wordmark_line2: "SOUJANYA",
    footer_note: "Limited pieces, made for the moments you keep.",
    address: "Nizampet Main Rd, Survey 37, Nagarjuna Homes, Nizampet, Hyderabad, Telangana 500085.",
  },
  about: {
    heading: "Where Indian heritage is reimagined through a contemporary lens.",
    body:
      "Lalita Soujanya is a contemporary fashion label rooted in Indian craftsmanship and expressed through modern fusion silhouettes, refined detailing and effortless elegance.\n\n" +
      "Our design philosophy is inspired by the richness of Indian fashion, yet intentionally moves beyond traditional boundaries. Drapes, textures, embroideries and artisanal techniques are reinterpreted through contemporary cuts, fluid forms and modern styling—creating pieces that feel distinctly Indian, yet effortlessly relevant to today.\n\n" +
      "From sophisticated festive wear and occasion dressing to statement fusion ensembles, each design is created to celebrate individuality, movement and modern expression. Our pieces are designed for those who appreciate the beauty of Indian craftsmanship while seeking a fresh, contemporary way to wear it.\n\n" +
      "Our current catalogue represents the beginning of this journey—a curated collection of distinctive designs created with thoughtful detailing, modern silhouettes and a balance of luxury, comfort and individuality.",
    image: null,
  },
  customization: {
    heading: "Made for you.",
    body: "Customization is at the heart of the house. Bring us a fabric, a memory, a colour or a silhouette — we translate. Because these are made-to-order, customized pieces require full advance payment and are non-cancellable, non-returnable and non-refundable once confirmed.",
  },
};

function repairDisplayText(value) {
  if (typeof value === "string") {
    return value
      .replace(/\u00c2\u00b7/g, "\u00b7")
      .replace(/\u00c2\u00a9/g, "\u00a9")
      .replace(/\u00e2\u0080\u0094/g, "\u2014")
      .replace(/\u00e2\u0080\u0093/g, "\u2013")
      .replace(/\u00e2\u0080\u00a6/g, "\u2026")
      .replace(/\u00e2\u0082\u00b9/g, "\u20b9");
  }
  if (Array.isArray(value)) return value.map(repairDisplayText);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, repairDisplayText(item)])
    );
  }
  return value;
}

function mergeDefaults(defaults, saved) {
  if (!defaults || typeof defaults !== "object" || Array.isArray(defaults)) return saved ?? defaults;
  const result = { ...defaults, ...(saved || {}) };
  Object.keys(defaults).forEach((key) => {
    if (defaults[key] && typeof defaults[key] === "object" && !Array.isArray(defaults[key])) {
      result[key] = mergeDefaults(defaults[key], saved?.[key]);
    }
  });
  return result;
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_BRAND);
  const [homepage, setHomepage] = useState(DEFAULT_HOMEPAGE);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [s, h] = await Promise.all([api.get("/api/settings"), api.get("/api/homepage")]);
      setSettings(mergeDefaults(DEFAULT_BRAND, repairDisplayText(s.data)));
      setHomepage(mergeDefaults(DEFAULT_HOMEPAGE, repairDisplayText(h.data)));
    } catch {
      // keep defaults
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const whatsappLink = (message) => {
    const num = (settings.whatsapp_number || "").replace(/\D/g, "");
    return `https://wa.me/${num}${message ? `?text=${encodeURIComponent(message)}` : ""}`;
  };

  return (
    <SettingsContext.Provider value={{ settings, homepage, setSettings, setHomepage, refresh, loaded, whatsappLink }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used within SettingsProvider");
  return ctx;
}
